import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {LoadWrapper} from 'components/LoadWrapper';
import _ from 'lodash';
import {BaselineBreadcrumbs} from '../utils/BaselineBreadcrumbs';
import {ProjectDetailsPageParams} from 'features/VehicleProjects/vehicleProjectPagePaths';
import {
	GetBaselineByIdWithAssignedReqsQuery,
	useGetBaselineByIdWithAssignedReqsLazyQuery,
} from '../hooks/baseline.generated';
import {useTranslation} from 'react-i18next';
import {
	ComparisonResult,
	generateComparisonResult,
} from './BaselineComparisonPage.utils';
import {
	DetailsRowCompat as DetailsRow,
	IconButtonCompat as IconButton,
	IDetailsRowProps,
	SelectionMode,
	StackCompat as Stack,
	useTheme,
} from 'components/FluentUICompat';
import {BaselinesPicker} from './BaselinesSelectionDialog/BaselinesPicker/BaselinesPicker';
import {PossibleSelectedBaseline} from './BaselinesSelectionDialog/BaselinesSelectionDialog.types';
import {createTooltipTranslationProviderFromNamespace} from 'features/localizedTooltips';
import {EntityList} from 'components';
import {useComparedAssignedRequirementsColumn} from './BaselineComparisonColumn';
import {EntityListContextProvider} from 'components/EntityList/EntityListContext';

export function BaselineComparisonPage(): React.ReactElement {
	const params = useParams();
	const {t} = useTranslation('features/VehicleProjects/baselineChanges');

	const theme = useTheme();

	const baselineId1 = params.id;
	const baselineId2 = params.idOfBaselineToCompareTo;

	const [getBaselineData1, {data: baselineData1, loading: loading1}] =
		useGetBaselineByIdWithAssignedReqsLazyQuery();

	const [getBaselineData2, {data: baselineData2, loading: loading2}] =
		useGetBaselineByIdWithAssignedReqsLazyQuery();

	const [baselineId1Empty, setBaselineId1Empty] = useState(!baselineId1);
	const [baselineId2Empty, setBaselineId2Empty] = useState(!baselineId2);

	const updateBaseline1 = (baselineId: string | undefined) => {
		if (baselineId) {
			getBaselineData1({variables: {id: baselineId}});
		}
		setBaselineId1Empty(!baselineId);
	};

	const updateBaseline2 = (baselineId: string | undefined) => {
		if (baselineId) {
			getBaselineData2({variables: {id: baselineId}});
		}
		setBaselineId2Empty(!baselineId);
	};

	useEffect(() => {
		updateBaseline1(baselineId1);
		updateBaseline2(baselineId2);
	}, [baselineId1, baselineId2]);

	const comparisonResult = useMemo(() => {
		return generateComparisonResult(baselineData1, baselineData2);
	}, [baselineData1, baselineData2]);

	const columns = useComparedAssignedRequirementsColumn();

	const pageDetails = useMemo(
		() => ({
			b1Name: baselineData1?.baselineById?.name,
			b2Name: baselineData2?.baselineById?.name,
		}),
		[baselineData1, baselineData2],
	);
	if (loading1 || loading2) return <LoadWrapper loading />;

	return (
		<>
			<BaselineBreadcrumbs
				baselineName={t('BaselineCompare')}
				params={params as ProjectDetailsPageParams}
			/>
			<BaselineCompareHeader
				baselineData1={baselineId1Empty ? undefined : baselineData1}
				baselineData2={baselineId2Empty ? undefined : baselineData2}
				updateBaseline1={updateBaseline1}
				updateBaseline2={updateBaseline2}
			/>
			<BaselineCompareTooltipTranslationProvider>
				<EntityListContextProvider pageDetails={pageDetails}>
					<EntityList
						columns={columns}
						items={comparisonResult}
						selectionMode={SelectionMode.none}
						styles={{root: {paddingLeft: 10}}}
						onRenderRow={renderBaselineComparisonRow}
					/>
				</EntityListContextProvider>
			</BaselineCompareTooltipTranslationProvider>
		</>
	);
}

interface BaselineCompareHeaderProps {
	baselineData1: GetBaselineByIdWithAssignedReqsQuery | undefined;
	baselineData2: GetBaselineByIdWithAssignedReqsQuery | undefined;
	updateBaseline1: (baselineId: string | undefined) => void;
	updateBaseline2: (baselineId: string | undefined) => void;
}

const BaselineCompareHeader: React.FC<BaselineCompareHeaderProps> = ({
	baselineData1,
	baselineData2,
	updateBaseline1,
	updateBaseline2,
}) => {
	const {t} = useTranslation('features/VehicleProjects/baselineChanges');
	return (
		<Stack horizontal horizontalAlign='space-evenly'>
			<Stack.Item style={{width: '40vw', margin: '10px'}}>
				<BaselinesPicker
					label={t('BaselineOld')}
					setBaseline={selectedBaseline => {
						updateBaseline1((selectedBaseline as PossibleSelectedBaseline)?.id);
					}}
					possibleSelectedBaseline={baselineData1?.baselineById ?? null}
				/>
			</Stack.Item>
			<Stack.Item>
				<IconButton
					iconProps={{iconName: 'Switch'}}
					title='Switch'
					disabled={false}
					style={{marginTop: 60, padding: 20}}
					onClick={() => {
						updateBaseline1(baselineData2?.baselineById?.id);
						updateBaseline2(baselineData1?.baselineById?.id);
					}}
				/>
			</Stack.Item>
			<Stack.Item style={{width: '40vw', margin: '10px'}}>
				<BaselinesPicker
					label={t('BaselineNew')}
					setBaseline={selectedBaseline => {
						updateBaseline2((selectedBaseline as PossibleSelectedBaseline)?.id);
					}}
					possibleSelectedBaseline={baselineData2?.baselineById ?? null}
				/>
			</Stack.Item>
		</Stack>
	);
};

const BaselineCompareTooltipTranslationProvider =
	createTooltipTranslationProviderFromNamespace(
		'features/VehicleProjects/baselineChanges',
	);

const renderBaselineComparisonRow = (
	props?: IDetailsRowProps,
): React.ReactElement => {
	if (!props) return <></>;

	const isNotChanged =
		props.item.comparisonResult === ComparisonResult.Unchanged;

	const customStyles = {
		root: {
			backgroundColor: isNotChanged ? undefined : '#FFFCAA',
		},
	};

	return <DetailsRow {...props} styles={customStyles} />;
};
